// src/helpers/tickers/CrawlStaggeredTicker.ts
import gsap from "gsap"
import GSDevTools from "gsap/GSDevTools"
import type { AnimationI, TickerI } from "tweenly"
import { applyJoinedCrawlTexts } from "../subtimeline.helpers"
import { appendTickerCopy, removeTickerCopies, tickerCopyCount } from "../ticker/tickerCopies"
import { BaseTicker } from "./BaseTicker"
import { CrawlStaggeredCtx } from "./types"

gsap.registerPlugin(GSDevTools)

export class CrawlContinuousTicker extends BaseTicker {
    private readonly maskObject: TickerI
    private readonly animationGroup: AnimationI[]
    private readonly textListNode: HTMLElement | null

    private readonly maskWidth: number
    private readonly duration: number

    private lastWidth: number
    private newWidths: Record<string, number>
    private textsWidth: number
    private crawlSpeed: number

    constructor(args: CrawlStaggeredCtx) {
        super(args.timeline, args.objectId, args.joinedTexts, args.childTexts, args.assets)

        this.maskObject = args.maskObject
        this.animationGroup = args.animationGroup
        this.textListNode = args.textListNode

        this.maskWidth = args.maskWidth
        this.duration = args.duration

        this.lastWidth = args.lastWidth
        this.newWidths = args.newWidths
        this.textsWidth = args.textsWidth
        this.crawlSpeed = Number((this.maskWidth + this.textsWidth) / this.duration)
    }

    protected intro() {
        const { timeline, maskObject, maskWidth, duration, objectId, textListNode, textsWidth } =
            this
        this.lastWidth = 0
        const parentObject = maskObject.parentId
            ? document.getElementById(maskObject.parentId)
            : null
        const parentObjectWidth = parentObject?.clientWidth ? parentObject?.clientWidth : 0

        timeline.clear()
        timeline.set(`#${maskObject.id}`, {
            x: `${maskWidth}px`,
        })
        timeline.fromTo(
            `#${maskObject.id}`,
            { transform: `translateX(${maskWidth}px)` },
            {
                transform: `translateX(${0}px)`,
                ease: "none",
                duration: Number(maskWidth / duration),
            },
            0
        )
        if (textListNode) {
            const parent = textListNode.parentNode

            if (parent) {
                removeTickerCopies(objectId)
                const numberOfCopiesTextWrapper = tickerCopyCount(parentObjectWidth, textsWidth)
                for (let i = 0; i < numberOfCopiesTextWrapper; i++) {
                    const clone = appendTickerCopy(parent, textListNode, objectId, i)

                    timeline.fromTo(
                        `#${clone.id}`,
                        {
                            transform: `translateX(${maskWidth + textsWidth * (i + 1)}px)`,
                        },
                        {
                            transform: `translateX(${textsWidth + Number(textsWidth * i)}px)`,
                            ease: "none",
                            duration: Number(maskWidth / duration),
                        },
                        0
                    )
                }
            }
        }
        for (const childId of maskObject.childIds) {
            if (this.lastWidth === 0) {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${0}px)`,
                    },
                    0
                )
                this.lastWidth = this.newWidths[childId]
            } else {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${this.lastWidth}px)`,
                    },
                    0
                )
                this.lastWidth += this.newWidths[childId]
            }
        }

        timeline.set(`#${maskObject.id}`, {
            x: `0px`,
            onComplete: () => {
                this.repeat()
            },
        })
    }

    private repeat() {
        const { timeline, maskObject, duration, objectId, textListNode, textsWidth } = this
        this.lastWidth = 0
        const parentObject = maskObject.parentId
            ? document.getElementById(maskObject.parentId)
            : null
        const parentObjectWidth = parentObject?.clientWidth ? parentObject?.clientWidth : 0
        timeline.clear()
        timeline.set(`#${maskObject.id}`, {
            x: `0px`,
        })

        timeline.fromTo(
            `#${maskObject.id}`,
            { transform: `translateX(0px)` },
            {
                transform: `translateX(-${textsWidth}px)`,
                ease: "none",
                duration: Number(textsWidth / duration),
            },
            0
        )
        if (textListNode) {
            const parent = textListNode.parentNode

            if (parent) {
                removeTickerCopies(objectId)
                const numberOfCopiesTextWrapper = tickerCopyCount(parentObjectWidth, textsWidth)
                for (let i = 0; i <= numberOfCopiesTextWrapper; i++) {
                    const clone = appendTickerCopy(parent, textListNode, objectId, i)
                    timeline.set(`#${clone.id}`, {
                        transform: `translateX(${textsWidth * i}px)`,
                    })
                    timeline.fromTo(
                        `#${clone.id}`,
                        { transform: `translateX(${textsWidth * (i + 1)}px)` },
                        {
                            transform: `translateX(${textsWidth * i}px)`,
                            ease: "none",
                            duration: Number(textsWidth / duration),
                        },
                        0
                    )
                    timeline.fromTo(
                        `#${clone.id}`,
                        { transform: `translateX(${textsWidth * i}px)` },
                        {
                            transform: `translateX(${textsWidth * i - textsWidth}px)`,
                            ease: "none",
                            duration: Number(textsWidth / duration),
                        },
                        Number(textsWidth / duration)
                    )
                }
            }
        }
        for (const childId of maskObject.childIds) {
            if (this.lastWidth === 0) {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${0}px)`,
                    },
                    0
                )
                this.lastWidth = this.newWidths[childId]
            } else {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${this.lastWidth}px)`,
                    },
                    0
                )
                this.lastWidth += this.newWidths[childId]
            }
        }

        timeline.set(`#${maskObject.id}`, {
            x: "0px",
            onComplete: () => this.handleRepeat(() => this.repeat()),
        })
    }

    protected outro(nextTexts?: string) {
        const { timeline, maskObject, duration, objectId, textListNode, textsWidth } = this
        this.lastWidth = 0
        const parentObject = maskObject.parentId
            ? document.getElementById(maskObject.parentId)
            : null
        const parentObjectWidth = parentObject?.clientWidth ? parentObject?.clientWidth : 0
        timeline.clear()
        timeline.set(`#${maskObject.id}`, {
            x: `0px`,
        })
        const numberOfCopiesTextWrapper = tickerCopyCount(parentObjectWidth, textsWidth)
        timeline.fromTo(
            `#${maskObject.id}`,
            { transform: `translateX(0px)` },
            {
                transform: `translateX(-${textsWidth * (numberOfCopiesTextWrapper + 1)}px)`,
                ease: "none",
                duration: Number((textsWidth * (numberOfCopiesTextWrapper + 1)) / duration),
            },
            0
        )
        if (textListNode) {
            const parent = textListNode.parentNode
            if (parent) {
                removeTickerCopies(objectId)

                for (let i = 0; i < numberOfCopiesTextWrapper; i++) {
                    const clone = appendTickerCopy(parent, textListNode, objectId, i)
                    timeline.fromTo(
                        `#${clone.id}`,
                        { transform: `translateX(${textsWidth * (i + 1)}px)` },
                        {
                            transform: `translateX(${
                                textsWidth * i - textsWidth * numberOfCopiesTextWrapper
                            }px)`,
                            ease: "none",
                            duration: (textsWidth * (numberOfCopiesTextWrapper + 1)) / duration,
                        },
                        0
                    )
                }
            }
        }
        for (const childId of maskObject.childIds) {
            if (this.lastWidth === 0) {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${0}px)`,
                    },
                    0
                )
                this.lastWidth = this.newWidths[childId]
            } else {
                timeline.set(
                    `#${childId}`,
                    {
                        transform: `translateX(${this.lastWidth}px)`,
                    },
                    0
                )
                this.lastWidth += this.newWidths[childId]
            }
        }

        timeline.set(`#${this.maskObject.id}`, {
            x: "0px",
            onComplete: () => {
                if (nextTexts) {
                    this.currentJoinedTexts = nextTexts

                    const measured = applyJoinedCrawlTexts(
                        this.textListNode,
                        this.maskObject.childIds,
                        nextTexts
                    )
                    this.newWidths = measured.newWidths
                    this.textsWidth = measured.textsWidth
                }

                this.intro()
            },
        })
    }
}
